"use client";

import React from "react";
import { phCaptureException } from "@/utils/analytics";
import { isReactHookCountError } from "@/lib/transient-hook-error";


interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  fatal: boolean;
}

const FIRST_PARTY_SOURCE_FRAME = /\.(?:tsx|ts|jsx|mts|cts):\d+/;

function isVirtuosoStackOverflow(error: unknown): boolean {
  if (!(error instanceof RangeError)) return false;
  if (!error.message.includes("Maximum call stack size exceeded")) return false;

  const stack = error.stack ?? "";
  if (stack.includes("react-virtuoso") || stack.includes("react_virtuoso")) {
    return true;
  }
  return !FIRST_PARTY_SOURCE_FRAME.test(stack);
}

function isRecoverableVirtuosoError(error: unknown): boolean {
  return isVirtuosoStackOverflow(error) || isReactHookCountError(error);
}

export class VirtuosoErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, fatal: false };

  private recoveryPending = false;

  private hasReported = false;

  private recoveryFrame: ReturnType<typeof requestAnimationFrame> | null = null;

  private scheduleRecovery(): void {
    if (this.recoveryFrame !== null || this.state.fatal) return;

    this.recoveryFrame = requestAnimationFrame(() => {
      this.recoveryFrame = null;
      this.recoveryPending = true;
      this.setState({ hasError: false, error: null });
    });
  }

  static getDerivedStateFromError(error: Error): Partial<State> | null {
    if (isRecoverableVirtuosoError(error)) {
      return { hasError: true, error };
    }
    throw error;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (!this.hasReported) {
      this.hasReported = true;
      phCaptureException(error, {
        boundary: "virtuoso",
        recovered: true,
        componentStack: info.componentStack ?? undefined,
      });
    }

    if (this.recoveryPending) {
      this.recoveryPending = false;
      this.setState({ fatal: true });
    } else {
      this.scheduleRecovery();
    }

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "[VirtuosoErrorBoundary] Caught transient react-virtuoso error.",
        { error, info },
      );
    }
  }

  componentDidUpdate(_prevProps: Props, prevState: State): void {
    if (!this.state.hasError && prevState.hasError) {
      this.recoveryPending = false;
    }
  }

  componentWillUnmount(): void {
    if (this.recoveryFrame !== null) {
      cancelAnimationFrame(this.recoveryFrame);
      this.recoveryFrame = null;
    }
  }

  render(): React.ReactNode {
    if (this.state.fatal && this.state.error) {
      throw this.state.error;
    }
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
